package com.wintermar.inspection.feature.data.local.db

import com.wintermar.inspection.AppDatabase

object SqliteCompat {
    private fun getSqliteVersion(fetchVersion: () -> String?): String {
        val ver = try {
            fetchVersion()?.takeIf { it.isNotBlank() } ?: "0.0.0"
        } catch (_: Exception) {
            "0.0.0"
        }
        return ver
    }

    private fun isAtLeast(
        versionStr: String,
        major: Int = 3,
        minor: Int = 24,
        patch: Int = 0
    ): Boolean {
        val parts = versionStr.split('.').mapNotNull { it.toIntOrNull() }.toMutableList()
        while (parts.size < 3) parts.add(0)
        val (vMajor, vMinor, vPatch) = parts
        return when {
            vMajor != major -> vMajor > major
            vMinor != minor -> vMinor > minor
            else -> vPatch >= patch
        }
    }

    /**
     * Return true if runtime SQLite supports `INSERT ... ON CONFLICT(...) DO UPDATE` (>= 3.24.0)
     * Pass a lambda that runs your generated `select_sqlite_version` query and returns the string or null.
     */
    private fun checkSupportUpsert(fetchVersion: () -> String?): Boolean {
        val ver = getSqliteVersion(fetchVersion)
        return isAtLeast(ver)
    }

    /**
     * Select the appropriate insert implementation at runtime.
     *
     * - `upsert` and `replace` may be any type `T` (including function types with any signature).
     * - Returns the chosen value (`upsert` when runtime SQLite supports upsert, otherwise `replace`).
     *
     * Usage:
     * val exec = SqliteCompat.selectInsertImplementation(
     *   appDatabase = appDatabase,
     *   upsert = appDatabase.answerOptionTableQueries::insertAnswerOption,
     *   replace = appDatabase.answerOptionTableQueries::insertReplaceAnswerOption
     * )
     * // call `exec(...)` with positional args matching the function signature
     */
    fun <F> selectInsertImplementation(
        appDatabase: AppDatabase,
        upsert: F,
        replace: F
    ): F {
        val version = appDatabase.sqliteVersionQueries.select_sqlite_version().executeAsOne()
        val supportUpsert = checkSupportUpsert {
            version
        }
        return if (supportUpsert) upsert else replace
    }
}
